/**
 * The slate shelf: up to six presets, each with a title, that become one
 * printed page.
 *
 * Six is not arbitrary. A 2x3 grid on A4 leaves each code about 55 mm across,
 * which is comfortably above the ~40 mm a HERO needs to read reliably, and
 * still leaves room for a title and a line of conditions under it. Seven codes
 * on a page means codes too small to scan in poor light, which is the only
 * light a printed slate is ever read in.
 */

import type { DiveConditions } from "./conditions";
import { DEFAULT_CONDITIONS, summarise, suggestTitle } from "./conditions";
import { compileConditions } from "./compiler";

export const MAX_SLOTS = 6;

export interface ShelfSlot {
  id: string;
  /** The diver's own name for it. This is what they read at the boat rail. */
  title: string;
  conditions: DiveConditions;
  addedAt: number;
}

export interface CompiledSlot extends ShelfSlot {
  payload: string;
  summary: string;
  /** True if the code compiled without errors. A slot with errors is still
   *  shown, but is excluded from the PDF rather than printed broken. */
  ok: boolean;
}

export function newSlot(conditions: DiveConditions, title?: string): ShelfSlot {
  return {
    id: crypto.randomUUID(),
    title: (title ?? suggestTitle(conditions)).slice(0, 40),
    conditions: { ...conditions },
    addedAt: Date.now(),
  };
}

export function compileSlot(slot: ShelfSlot): CompiledSlot {
  const result = compileConditions(slot.conditions);
  return {
    ...slot,
    payload: result.payload,
    summary: summarise(slot.conditions),
    ok: result.ok,
  };
}

export function compileShelf(slots: readonly ShelfSlot[]): CompiledSlot[] {
  return slots.map(compileSlot);
}

// --- persistence ----------------------------------------------------------
//
// localStorage, not IndexedDB: the shelf is at most six small objects, and it
// needs to survive a reload on a phone that has been in a dry bag. Dexie and
// the sync outbox (DESIGN.md v2) sit above this, not in place of it.

const STORAGE_KEY = "dive-slate.shelf.v1";

export function loadShelf(): ShelfSlot[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(migrateSlot).filter(isShelfSlot).slice(0, MAX_SLOTS);
  } catch {
    // A corrupt shelf must never stop the builder from working.
    return [];
  }
}

/** Backfills `DiveConditions` fields a shelf slot might predate, so
 *  `isShelfSlot` doesn't either reject an old shelf outright or — worse —
 *  silently accept it with a field missing at runtime despite the type
 *  claiming it's required. Handles every generation seen so far: the
 *  `persist` -> `persistenceMode` rename (a slot saved before that still has
 *  the old field name), slots old enough to predate persistence entirely
 *  (neither field present), and slots that predate `evComp`. A slot missing
 *  `evComp` isn't just a type mismatch — `engine.ts`'s `evToken` indexes a
 *  `Record<EvComp, string>` with it, so `undefined` would throw. A future
 *  `DiveConditions` field that isn't safe to default should get a line here
 *  too, not a separate special case elsewhere. */
function migrateSlot(value: unknown): unknown {
  if (typeof value !== "object" || value === null) return value;
  const v = value as Record<string, unknown>;
  const conditions = v["conditions"];
  if (typeof conditions !== "object" || conditions === null) return value;
  const c = conditions as Record<string, unknown>;
  const { persist, ...rest } = c;

  const persistenceMode =
    typeof c["persistenceMode"] === "string"
      ? c["persistenceMode"]
      : typeof persist === "string"
        ? persist
        : DEFAULT_CONDITIONS.persistenceMode;
  const evComp = typeof c["evComp"] === "number" ? c["evComp"] : DEFAULT_CONDITIONS.evComp;

  return { ...v, conditions: { ...rest, persistenceMode, evComp } };
}

export function saveShelf(slots: readonly ShelfSlot[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slots.slice(0, MAX_SLOTS)));
  } catch {
    // Private mode, quota, a locked-down webview. Losing the shelf on reload is
    // survivable; crashing the app over it is not.
  }
}

function isShelfSlot(value: unknown): value is ShelfSlot {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (
    typeof v["id"] !== "string" ||
    typeof v["title"] !== "string" ||
    typeof v["conditions"] !== "object" ||
    v["conditions"] === null
  ) {
    return false;
  }
  const c = v["conditions"] as Record<string, unknown>;
  return typeof c["depthM"] === "number" && typeof c["persistenceMode"] === "string";
}
